import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";

/**
 * Selector múltiple (uno o más valores).
 * options: array de strings o de { value, label }.
 * selected: array de values. onChange(nextArray).
 */
export default function MultiSelect({ options = [], selected = [], onChange, placeholder = "Todos" }) {
  const norm = useMemo(
    () => options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );
  const toggle = (v) =>
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);

  const allSelected = norm.length > 0 && selected.length === norm.length;
  const text =
    norm.length === 0
      ? placeholder
      : allSelected
        ? "Todas seleccionadas"
        : selected.length === 0
          ? "Ninguna seleccionada"
          : selected.length === 1
            ? norm.find((o) => o.value === selected[0])?.label || selected[0]
            : `${selected.length} de ${norm.length}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 w-full justify-between text-xs font-normal px-3">
          <span className="truncate">{text}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-50 shrink-0 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="flex gap-2 mb-1.5">
          <Button variant="ghost" size="sm" className="h-7 text-xs px-2"
            onClick={() => onChange(norm.map((o) => o.value))}>Todas</Button>
          <Button variant="ghost" size="sm" className="h-7 text-xs px-2"
            onClick={() => onChange([])}>Ninguna</Button>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
          {norm.length === 0 && <p className="text-xs text-gray-400 px-1">Sin opciones</p>}
          {norm.map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
              <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              <span className="text-gray-700">{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
